import { Loader2, Search } from "lucide-react";
import type { ReactNode } from "react";
import { MAIL_APP_HEADER_TITLE } from "@/auth/constants";
import { MailHeaderBrandIcon } from "@/features/shell/MailHeaderBrandIcon";

type Props = {
  actions?: ReactNode;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  searchDisabled?: boolean;
  searchBusy?: boolean;
  /** Une seule ligne titre + actions (layout mobile). */
  variant?: "default" | "mobile";
};

export function MailShellHeader({
  actions,
  searchQuery = "",
  onSearchChange,
  onSearchSubmit,
  searchDisabled = false,
  searchBusy = false,
  variant = "default",
}: Props) {
  const showSearch = !searchDisabled && onSearchChange != null;
  const SearchIcon = searchBusy ? Loader2 : Search;
  const isMobile = variant === "mobile";

  return (
    <header
      className={
        isMobile ? "mcv-app-header mcv-app-header--mobile shrink-0" : "mcv-app-header shrink-0"
      }
    >
      <div className="mcv-app-header__inner gap-2 px-3 py-2 sm:gap-3 sm:px-5 sm:py-3">
        <div className="mcv-app-header__brand min-w-0 shrink-0">
          <span className="mcv-app-header__icon-ring" aria-hidden="true">
            <MailHeaderBrandIcon />
          </span>
          <div className="mcv-app-header__title-stack min-w-0">
            <h1
              className={
                isMobile
                  ? "mcv-app-header__title truncate"
                  : "mcv-app-header__title truncate max-sm:max-w-[5.5rem] sm:max-w-none"
              }
            >
              {MAIL_APP_HEADER_TITLE}
            </h1>
          </div>
        </div>

        {showSearch ? (
          <div className="relative mx-auto min-w-0 max-w-md flex-1 max-sm:hidden">
            <label className="relative block w-full">
              <SearchIcon
                className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${searchBusy ? "animate-spin text-[var(--mail-accent)]" : "text-[var(--mail-muted)]"}`}
                aria-hidden
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onSearchSubmit?.();
                }}
                placeholder="Rechercher…"
                className="w-full rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] py-2 pl-9 pr-3 text-sm text-[var(--mail-text)] placeholder:text-[var(--mail-muted)]"
              />
            </label>
          </div>
        ) : null}

        <div className="mcv-app-header__actions flex shrink-0 items-center gap-2">
          {actions}
        </div>
      </div>

      {showSearch ? (
        <div className="block border-t border-[var(--mail-border-soft)] px-3 py-2 sm:hidden">
          <label className="relative block">
            <SearchIcon
              className={`pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 ${searchBusy ? "animate-spin text-[var(--mail-accent)]" : "text-[var(--mail-muted)]"}`}
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearchSubmit?.();
              }}
              placeholder="Rechercher…"
              className="w-full rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] py-2 pl-9 pr-3 text-sm text-[var(--mail-text)] placeholder:text-[var(--mail-muted)]"
            />
          </label>
        </div>
      ) : null}
    </header>
  );
}
